import React, { useEffect, lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import ReactGA from 'react-ga4';

// Eager Shell & Layout Primitives (Statically Loaded for Instant Render)
import Home from '../Components/Home/Home';
import Nav from '../Components/Nav/Nav';
import Footer from '../Components/footer/Footer';
import RouteLoadingFallback from '../Components/Common/RouteLoadingFallback';
import RouteErrorBoundary from '../Components/Common/RouteErrorBoundary';

// Dynamic Route Components (Code-Split into Named Webpack Chunks)
const SignupPage = lazy(() => import(/* webpackChunkName: "marketing-auth" */ '../Components/Signup/SingupPage'));
const NewsPage = lazy(() => import(/* webpackChunkName: "marketing-news" */ '../Components/News/News'));
const Upload = lazy(() => import(/* webpackChunkName: "feature-upload" */ '../Components/upload/Upload'));
const UploadPage = lazy(() => import(/* webpackChunkName: "feature-upload" */ '../Components/upload/UploadPage'));

const ChoosePlan = lazy(() => import(/* webpackChunkName: "billing-subscriptions" */ '../Components/ChoosePlan/ChoosePlan'));
const Greetings = lazy(() => import(/* webpackChunkName: "marketing-misc" */ '../Components/Greetings/Greetings'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "marketing-legal" */ '../Components/PrivacyPolicy/PrivacyPolicy'));
const TermsService = lazy(() => import(/* webpackChunkName: "marketing-legal" */ '../Components/TermService/TermsService'));
const RefundPolicy = lazy(() => import(/* webpackChunkName: "marketing-legal" */ '../Components/RefundPolicy/RefundPolicy'));
const AboutUs = lazy(() => import(/* webpackChunkName: "marketing-legal" */ '../Components/About_Us/AboutUs'));

const ReportOutlet = lazy(() => import(/* webpackChunkName: "reports-analytics" */ '../Components/Reports/ReportOutlet/ReportOutlet'));
const ReportContent = lazy(() => import(/* webpackChunkName: "reports-analytics" */ '../Components/Reports/ReportContent/ReportContent'));
const YourApproach = lazy(() => import(/* webpackChunkName: "reports-analytics" */ '../Components/Reports/ReportApproach/YourApproach'));
const CorrectApproach = lazy(() => import(/* webpackChunkName: "reports-analytics" */ '../Components/Reports/ReportApproach/CorrectApproach'));

const ProfileOutlet = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/ProfileOutlet/ProfileOutlet'));
const PersonalDetails = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/PersonalDetails/PersonalDetails'));
const UpdateProfile = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/UpdateProfile/UpdateProfile'));
const Membership = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/Membership/Membership'));
const ProfileReport = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/ProfileReport/ProfileReport'));
const Support = lazy(() => import(/* webpackChunkName: "user-profile" */ '../Components/Profile/Support/Support'));

const SubscriptionPricing = lazy(() => import(/* webpackChunkName: "billing-subscriptions" */ '../Components/SubscriptionPricing/SubscriptionPricing'));
const SubscriptionSuccess = lazy(() => import(/* webpackChunkName: "billing-subscriptions" */ '../Components/SubscriptionSuccess/SubscriptionSuccess'));
const SubscriptionFailure = lazy(() => import(/* webpackChunkName: "billing-subscriptions" */ '../Components/SubscriptionFailure/SubscriptionFailure'));

const GamePostOutlet = lazy(() => import(/* webpackChunkName: "game-posts" */ '../Components/GamePost/GamePostOutlet/GamePostOutlet'));
const GameVideos = lazy(() => import(/* webpackChunkName: "game-posts" */ '../Components/GamePost/GameVideos/GameVideos'));
const GameReports = lazy(() => import(/* webpackChunkName: "game-posts" */ '../Components/GamePost/GameReports/GameReports'));
const GameSupport = lazy(() => import(/* webpackChunkName: "game-posts" */ '../Components/GamePost/GameSupport/GameSupport'));

const GameFeedbackOutlet = lazy(() => import(/* webpackChunkName: "game-feedback" */ '../Components/GameFeedback/GameFeedbackOutlet/GameFeedbackOutlet'));
const GameCoachFeedback = lazy(() => import(/* webpackChunkName: "game-feedback" */ '../Components/GameFeedback/GameCoachFeedback/GameCoachFeedback'));
const Highlights = lazy(() => import(/* webpackChunkName: "game-feedback" */ '../Components/GameFeedback/Highlights/Highlights'));
const Stats = lazy(() => import(/* webpackChunkName: "game-feedback" */ '../Components/GameFeedback/Stats/Stats'));

const LandingPage = lazy(() => import(/* webpackChunkName: "coach-flow" */ '../Components/CoachFlow/landingPage'));
const Pending = lazy(() => import(/* webpackChunkName: "coach-flow" */ '../Components/CoachFlow/Pending/Pending'));
const Reviewd = lazy(() => import(/* webpackChunkName: "coach-flow" */ '../Components/CoachFlow/Reviewd/Reviewd'));
const UsersPostsTable = lazy(() => import(/* webpackChunkName: "coach-flow" */ '../Components/CoachFlow/Academy/academy_report'));

const Requestfeedback = lazy(() => import(/* webpackChunkName: "video-feedback" */ '../Components/GamePost/RequestFeedback/Requestfeedback'));
const CreateFeedback = lazy(() => import(/* webpackChunkName: "video-feedback" */ '../Components/Video.js/create-feedback/CreateFeedback'));
const ReviewFeedback = lazy(() => import(/* webpackChunkName: "video-feedback" */ '../Components/Video.js/review-feedback/ReviewFeedback'));
const Playground = lazy(() => import(/* webpackChunkName: "smart-insights-playground" */ '../Components/Playground/Playground'));

const Products = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/Products/Products'));
const SmartAssessments = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/Products/SmartAssessments'));
const SmartInsights = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/Products/SmartInsights'));
const SmartJournals = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/Products/SmartJournals'));
const Solutions = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/Solutions/Solutions'));
const Careers = lazy(() => import(/* webpackChunkName: "marketing-misc" */ '../Components/Careers/Careers'));
const CaseStudies = lazy(() => import(/* webpackChunkName: "marketing-misc" */ '../Components/CaseStudies/CaseStudies'));
const ExploreProducts = lazy(() => import(/* webpackChunkName: "marketing-products" */ '../Components/ExploreProducts/ExploreProducts'));

// Helper to scroll to top on route change
const ScrollToTop = () => {
    const location = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);
    return null;
};

// Global gate to force users to complete profile on first sign-in or when required fields are missing
const ProfileGate = () => {
    const env = process.env.REACT_APP_ENV;
    const location = useLocation();
    const profileState = useSelector((state) => state.user?.profile || {});
    const profileValue = profileState?.value || {};
    const pd = profileValue?.profileData || {};
    const email = profileValue?.email?.email;
    const name = pd?.name || {};
    const gender = pd?.gender;
    const dob = pd?.dob;

    if (env !== 'users') return null;

    let token = null;
    let profileCompletedFlag = false;
    try {
        token = localStorage.getItem('BQS_TOKEN');
        profileCompletedFlag = localStorage.getItem('PROFILE_COMPLETED') === '1';
    } catch { }

    if (!token) return null;

    let dobValid = false;
    if (dob && /^\d{2}-\d{2}-\d{4}$/.test(dob)) {
        const dt = DateTime.fromFormat(dob, 'dd-MM-yyyy');
        if (dt.isValid && dt <= DateTime.now().startOf('day')) dobValid = true;
    }
    const hasName = Boolean(name?.firstName && name?.lastName);
    const isComplete = Boolean(email && hasName && gender && dobValid);

    if (profileCompletedFlag || isComplete) {
        if (!profileCompletedFlag) {
            try { localStorage.setItem('PROFILE_COMPLETED', '1'); } catch { }
        }
    } else {
        if (!profileState.isStarted && !profileState.isError && Object.keys(profileValue).length > 0) {
            try { localStorage.setItem('PROFILE_COMPLETED', '0'); } catch { }
        }
    }

    let allowUpdate = false;
    try { allowUpdate = sessionStorage.getItem('ALLOW_PROFILE_UPDATE') === '1'; } catch { }

    if (!allowUpdate && location.pathname !== '/update-profile') {
        try { sessionStorage.removeItem('ALLOW_PROFILE_UPDATE'); } catch { }
    }

    return null;
};

// Track GA4 page views for each route change (works with HashRouter)
const GAListener = () => {
    const location = useLocation();
    useEffect(() => {
        const page = `${location.pathname}${location.search}${location.hash}`;
        ReactGA.send({ hitType: 'pageview', page });
    }, [location.pathname, location.search, location.hash]);
    return null;
};

// Trailing slash remover to put just inside BrowserRouter
const RemoveTrailingSlash = () => {
    const location = useLocation();
    if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
        return (
            <Navigate
                to={{
                    pathname: location.pathname.replace(/\/+$/, ''),
                    search: location.search,
                    hash: location.hash,
                }}
                replace
            />
        );
    }
    return null;
};

// Global gate to force users to upgrade if their First Step plan has expired
const SubscriptionGate = () => {
    const location = useLocation();
    const allSubs = useSelector((state) => state.user?.allSubscriptions?.value);
    const activeSubs = useSelector((state) => state.user?.subscriptions?.value);
    const profileValue = useSelector((state) => state.user?.profile?.value || {});

    const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
    if (enforcement === 'off') {
        return null;
    }

    const userRole = localStorage.getItem('BQS_ROLE');
    if (userRole === 'coach') {
        return null;
    }

    let token = null;
    try { token = localStorage.getItem('BQS_TOKEN'); } catch { }

    if (!token) return null;
    if (!Array.isArray(allSubs) || !Array.isArray(activeSubs)) return null;

    const hasExpiredFirstStep = allSubs.some(sub => {
        if (sub.plan?.code === 'insights-first-step') {
            const isActive = activeSubs.some(active => active._id === sub._id);
            return !isActive;
        }
        return false;
    });

    const hasActivePlan = activeSubs.length > 0;
    const membershipProfile = profileValue?.profileData?.membershipProfile;
    const hasActiveSubscription = hasActivePlan || (membershipProfile && (membershipProfile.isActive === true || (membershipProfile.remainingReviews ?? 0) > 0));

    if (hasExpiredFirstStep && !hasActivePlan && location.pathname !== '/upgrade') {
        const allowedPaths = ['/login', '/signup', '/greetings', '/privacy_policy', '/term_service', '/about', '/subscription-success', '/subscription-failure'];
        if (!allowedPaths.includes(location.pathname)) {
            return <Navigate to="/upgrade" replace />;
        }
    }
    
    const isTestMode = location.search.includes("test=1");
    if (location.pathname === '/upgrade' && hasActivePlan && !isTestMode) {
        return <Navigate to="/game-posts/game-videos" replace />;
    }

    if (!hasActiveSubscription) {
        const protectedPaths = ['/game-posts', '/upload', '/game-feedback', '/reports'];
        const isProtectedPath = protectedPaths.some(path => location.pathname.startsWith(path)) && 
                                !location.pathname.startsWith('/game-posts/subscription-pricing');
        if (isProtectedPath) {
            return <Navigate to="/subscription-pricing" replace />;
        }
    }

    return null;
};

// Inner component so useLocation() is available inside HashRouter
function RouterContent() {
    const env = process.env.REACT_APP_ENV;
    const location = useLocation();
    const isStandaloneUpload = location.pathname === '/upload-standalone' || location.pathname === '/game-posts/upload';

    return (
        <>
            <ScrollToTop />
            <GAListener />
            <RemoveTrailingSlash />
            {env === "users" && !isStandaloneUpload && <ProfileGate />}
            {env === "users" && !isStandaloneUpload && <SubscriptionGate />}
            {!isStandaloneUpload && <Nav />}
            <RouteErrorBoundary>
                <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                        <Route index element={<Home />} />
                        <Route path="signup" element={<SignupPage />} />
                        <Route path="/SmartInsights" element={<Playground />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/products/smart-assessments" element={<SmartAssessments />} />
                        <Route path="/products/smart-insights" element={<SmartInsights />} />
                        <Route path="/products/smart-journals" element={<SmartJournals />} />
                        <Route path="/solutions" element={<Solutions />} />
                        <Route path="/customers" element={<Navigate to="/solutions" replace />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/case-studies" element={<CaseStudies />} />
                        <Route path="/explore" element={<ExploreProducts />} />
                        <Route path="support" element={<GameSupport />} />
                        {env !== "standalone" ? (
                            <>
                                <Route path="update-profile" element={<UpdateProfile />} />
                                <Route path="upgrade" element={<SubscriptionPricing isForceUpgrade={true} />} />
                                <Route path="news" element={<NewsPage />} />
                                <Route path="blogs" element={<Navigate to="/news" replace />} />
                                <Route path="upload" element={<Upload />} />
                                <Route path="upload-standalone" element={<UploadPage />} />
                                <Route path="choose_plan" element={<ChoosePlan />} />
                                <Route path="subscription-pricing" element={<SubscriptionPricing />} />
                                <Route path="subscription-success" element={<SubscriptionSuccess />} />
                                <Route path="subscription-failure" element={<SubscriptionFailure />} />
                                <Route path="signup" element={<SignupPage />} />
                                <Route path='game-posts' element={<GamePostOutlet />}>
                                    <Route index element={<Navigate to="game-videos" replace />} />
                                    <Route path='game-videos' element={<GameVideos />} />
                                    <Route path='game-videos/feedback/:postId' element={<ReviewFeedback />} />
                                    <Route path='game-videos/request/:postId' element={<Requestfeedback />} />
                                    <Route path='game-reports' element={<GameReports />} />
                                    <Route path='game-reports/feedback/:postId' element={<ReviewFeedback />} />
                                    <Route path='subscription-pricing' element={<SubscriptionPricing />} />
                                    <Route path='game-supports' element={<GameSupport />} />
                                    <Route path='upload' element={<UploadPage />} />
                                </Route>

                                <Route path="profile" element={<ProfileOutlet />}>
                                    <Route index element={<PersonalDetails />} />
                                    <Route path='personal_details' element={<PersonalDetails />} />
                                    <Route path='membership' element={<Membership />} />
                                    <Route path='report' element={<ProfileReport />} />
                                    <Route path='support' element={<Support />} />
                                </Route>

                                <Route path="game-feedback" element={<GameFeedbackOutlet />}>
                                    <Route index element={<GameCoachFeedback />} />
                                    <Route path="coach-feedback" element={<GameCoachFeedback />} />
                                    <Route path="highlights" element={<Highlights />} />
                                    <Route path="stats" element={<Stats />} />
                                </Route>

                                <Route path="greetings" element={<Greetings />} />
                                <Route path="privacy_policy" element={<PrivacyPolicy />} />
                                <Route path="term_service" element={<TermsService />} />
                                <Route path="refund_policy" element={<RefundPolicy />} />
                                <Route path="about" element={<AboutUs />} />

                                <Route path="reports/:reviewPostId" element={<ReportOutlet />}>
                                    <Route index element={<ReportContent />} />
                                    <Route path="uploaded-video" element={<ReportContent />} />
                                    <Route path=":technique/your-approach" element={<YourApproach />} />
                                    <Route path=":technique/correct-approach" element={<CorrectApproach />} />
                                </Route>

                                <Route path="coach_flow" element={<LandingPage />}>
                                    <Route index element={<Navigate to="reviewd-video" replace />} />
                                    <Route path="pending-video" element={<Pending />} />
                                    <Route path="pending-video/feedback/:postId" element={<CreateFeedback />} />
                                    <Route path="reviewd-video" element={<Reviewd />} />
                                    <Route path="reviewd-video/feedback/:postId" element={<ReviewFeedback />} />
                                    <Route path="academy-reports" element={<UsersPostsTable />} />
                                </Route>
                            </>
                        ) : (
                            <>
                                <Route path="game-posts" element={<GamePostOutlet />}>
                                    <Route index element={<Navigate to="game-videos" replace />} />
                                    <Route path="game-videos" element={<GameVideos />} />
                                    <Route path='game-videos/feedback/:postId' element={<ReviewFeedback />} />
                                    <Route path='game-videos/request/:postId' element={<Requestfeedback />} />
                                    <Route path="game-reports" element={<GameReports />} />
                                    <Route path='game-reports/feedback/:postId' element={<ReviewFeedback />} />
                                    <Route path="subscription-pricing" element={<SubscriptionPricing />} />
                                    <Route path="game-supports" element={<GameSupport />} />
                                    <Route path='upload' element={<UploadPage />} />
                                </Route>
                                <Route path="coach_flow" element={<LandingPage />}>
                                    <Route index element={<Navigate to="reviewd-video" replace />} />
                                    <Route path="pending-video" element={<Pending />} />
                                    <Route path="pending-video/feedback/:postId" element={<CreateFeedback />} />
                                    <Route path="reviewd-video" element={<Reviewd />} />
                                    <Route path="reviewd-video/feedback/:postId" element={<ReviewFeedback />} />
                                </Route>
                                <Route path="*" element={<Navigate to="/" />} />
                            </>
                        )}
                        <Route path="upload-standalone" element={<UploadPage />} />
                    </Routes>
                </Suspense>
            </RouteErrorBoundary>
            {!isStandaloneUpload && <Footer />}
        </>
    );
}

export default function Routers() {
    return (
        <HashRouter>
            <RouterContent />
        </HashRouter>
    );
}
